import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  CircularProgress,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Grid,
  Tooltip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { 
  Refresh, 
  ChevronLeft, 
  ChevronRight,
  Visibility
} from '@mui/icons-material';
import { fetchWithdrawalRequests, updateWithdrawalStatus } from '../../services/api';
import { format } from 'date-fns';

const statusColors = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const WithdrawalsPage = () => {
  const { t } = useTranslation(['withdrawals', 'common']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [referenceFilter, setReferenceFilter] = useState('');
  const [debouncedReference, setDebouncedReference] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [updating, setUpdating] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0); // 0-based for frontend
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  const totalPages = Math.ceil(totalRequests / rowsPerPage) || 1;

  // Debounce logic
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReference(referenceFilter);
    }, 500);
    return () => clearTimeout(timer);
  }, [referenceFilter]);

  useEffect(() => {
    setPage(0);
  }, [debouncedReference]);

  const statusOptions = [
    { value: 'all', label: t('filters.all') },
    { value: 'pending', label: t('filters.pending') },
    { value: 'processing', label: t('filters.processing') },
    { value: 'completed', label: t('filters.completed') },
    { value: 'rejected', label: t('filters.rejected') },
    { value: 'cancelled', label: t('filters.cancelled') },
  ];

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (debouncedReference) {
        params.reference = debouncedReference;
      }
      const response = await fetchWithdrawalRequests(params);
      const results = response?.results || (Array.isArray(response) ? response : []);
      const count = response?.count || (Array.isArray(response) ? response.length : 0);
      
      setRequests(results);
      setTotalRequests(count);
    } catch (err) {
      console.error('Failed to load withdrawal requests', err);
      setError(t('messages.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedReference, page, rowsPerPage, t]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleOpen = (request) => {
    setSelected(request);
    setNote('');
  };

  const handleClose = () => {
    setSelected(null);
    setNote('');
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selected) return;
    try {
      setUpdating(true);
      await updateWithdrawalStatus(selected.id, {
        status: newStatus,
        admin_note: note,
      });
      handleClose();
      await loadRequests();
    } catch (err) {
      console.error('Failed to update withdrawal', err);
    } finally {
      setUpdating(false);
    }
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };
  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };
  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };
  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };
  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  // Add serial numbers
  const rowsWithSn = useMemo(() => {
    return requests.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));
  }, [requests, page, rowsPerPage]);

  const columns = [
    { field: 'sn', headerName: t('table.reference'), width: 80, sortable: false, filterable: false, renderCell: (params) => params.row.reference },
    {
      field: 'accountName',
      headerName: t('table.accountName', { defaultValue: 'Account Name' }),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payout_account?.account_name || '—'}
        </Typography>
      )
    },
    {
      field: 'accountNumber',
      headerName: t('table.accountNumber', { defaultValue: 'Account Number' }),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payout_account?.account_number || '—'}
        </Typography>
      )
    },
    {
      field: 'bankName',
      headerName: t('table.bankName', { defaultValue: 'Bank Name' }),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.payout_account?.bank_name || '—'}
        </Typography>
      )
    },
    {
      field: 'amount',
      headerName: t('table.payoutAmount'),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          ₦{Number(params.row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: 'processing_fee',
      headerName: t('table.processingFee'),
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Typography variant="body2">
          ₦{Number(params.row.processing_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: 'total_debit',
      headerName: t('table.totalDebit'),
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          ₦{Number(params.row.total_debit || params.row.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: t('table.status'),
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value === 'pending' ? t('filters.pending') : 
                 params.value === 'processing' ? t('filters.processing') :
                 params.value === 'completed' ? t('filters.completed') :
                 params.value === 'rejected' ? t('filters.rejected') :
                 params.value === 'cancelled' ? t('filters.cancelled') : params.value}
          color={statusColors[params.value] || 'default'}
          size="small"
        />
      )
    },
    {
      field: 'requested_at',
      headerName: t('table.requested'),
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value
            ? format(new Date(params.value), 'MMM d, yyyy h:mm a')
            : '—'}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('table.action'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={t('buttons.manage')}>
          <IconButton
            size="small"
            onClick={() => handleOpen(params.row)}
            color="primary"
          >
            <Visibility />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        px: { xs: 0.75, sm: 1, md: 1.5 },
        py: { xs: 2, sm: 2.5, md: 3 },
        maxWidth: '95vw',
        overflowX: 'hidden',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={3} spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('description')}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadRequests} disabled={loading} startIcon={<Refresh />}>
          {t('buttons.refresh')}
        </Button>
      </Stack>

      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label={t('filters.reference', { defaultValue: 'Reference' })}
                value={referenceFilter}
                onChange={(e) => setReferenceFilter(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search Reference"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                select
                size="small"
                label={t('filters.status')}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                fullWidth
              >
                {statusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ 
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
          }}>
            {loading && requests.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error" align="center" sx={{ py: 6 }}>
                {error}
              </Typography>
            ) : requests.length === 0 ? (
              <Typography align="center" color="text.secondary" sx={{ py: 6 }}>
                {t('messages.noRequests')}
              </Typography>
            ) : (
              <DataGrid
                rows={rowsWithSn}
                columns={columns}
                getRowId={(row) => row.id}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1000,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: theme => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Custom Pagination Bar */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: { xs: 'center', sm: 'flex-end' }, 
        mt: 3, 
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('pagination.page', { page: page + 1, total: totalPages, defaultValue: `Page ${page + 1} of ${totalPages}` })}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('pagination.rowsPerPage', { defaultValue: 'Rows per page:' })}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('pagination.custom', { defaultValue: 'Custom' })}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('pagination.rows', { defaultValue: 'Rows' })}
            />
          )}
        </Box>
      </Box>

      <Dialog open={Boolean(selected)} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialogs.manage.title')}</DialogTitle>
        {selected && (
          <>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.reference')}</Typography>
                  <Typography>{selected.reference}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.payoutAmount')}</Typography>
                  <Typography>₦{Number(selected.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.processingFee')}</Typography>
                  <Typography>₦{Number(selected.processing_fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.totalDebit')}</Typography>
                  <Typography fontWeight={600}>₦{Number(selected.total_debit || selected.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.account')}</Typography>
                  <Typography>
                    {selected.payout_account?.account_name} •{' '}
                    {selected.payout_account?.account_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selected.payout_account?.bank_name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">{t('dialogs.manage.status')}</Typography>
                  <Chip
                    label={selected.status === 'pending' ? t('filters.pending') : 
                           selected.status === 'processing' ? t('filters.processing') :
                           selected.status === 'completed' ? t('filters.completed') :
                           selected.status === 'rejected' ? t('filters.rejected') :
                           selected.status === 'cancelled' ? t('filters.cancelled') : selected.status}
                    color={statusColors[selected.status] || 'default'}
                    size="small"
                  />
                </Box>
                <Divider />
                <TextField
                  label={t('dialogs.manage.adminNote')}
                  multiline
                  minRows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={updating}>
                {t('buttons.close')}
              </Button>
              <Button
                onClick={() => handleUpdateStatus('processing')}
                color="info"
                disabled={updating}
              >
                {t('buttons.markProcessing')}
              </Button>
              <Button
                onClick={() => handleUpdateStatus('completed')}
                color="success"
                disabled={updating}
              >
                {t('buttons.markCompleted')}
              </Button>
              <Button
                onClick={() => handleUpdateStatus('rejected')}
                color="error"
                disabled={updating}
              >
                {t('buttons.reject')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WithdrawalsPage;
